// Client-side mirror of backend/sourcing_zone_geometry.py so the drawing
// toolbar can explain a problem before the operator tries to save.
export const MAX_ZONE_VERTICES = 30;

function orientation(a, b, c) {
  return (b.lng - a.lng) * (c.lat - a.lat) - (b.lat - a.lat) * (c.lng - a.lng);
}

function onSegment(a, b, point) {
  return Math.min(a.lng, b.lng) <= point.lng && point.lng <= Math.max(a.lng, b.lng)
    && Math.min(a.lat, b.lat) <= point.lat && point.lat <= Math.max(a.lat, b.lat);
}

function segmentsIntersect(a, b, c, d) {
  const o1 = orientation(a, b, c);
  const o2 = orientation(a, b, d);
  const o3 = orientation(c, d, a);
  const o4 = orientation(c, d, b);
  if (((o1 > 0 && o2 < 0) || (o1 < 0 && o2 > 0)) && ((o3 > 0 && o4 < 0) || (o3 < 0 && o4 > 0))) return true;
  return (o1 === 0 && onSegment(a, b, c)) || (o2 === 0 && onSegment(a, b, d))
    || (o3 === 0 && onSegment(c, d, a)) || (o4 === 0 && onSegment(c, d, b));
}

// points: open ring of {lat, lng}; the closing edge is implied.
export function validateZoneRing(points) {
  const count = points.length;
  if (count < 3) return { valid: false, count, message: `Add at least 3 corners (${count} so far).` };
  if (count > MAX_ZONE_VERTICES) {
    return { valid: false, count, message: `Use no more than ${MAX_ZONE_VERTICES} corners (${count} now).` };
  }
  const keys = new Set(points.map((point) => `${point.lat.toFixed(7)},${point.lng.toFixed(7)}`));
  if (keys.size !== count) return { valid: false, count, message: 'Two corners are in the same place.' };
  for (let first = 0; first < count; first += 1) {
    for (let second = first + 1; second < count; second += 1) {
      // Adjacent edges share a corner by design.
      if (second === first + 1 || (first === 0 && second === count - 1)) continue;
      if (segmentsIntersect(
        points[first], points[(first + 1) % count],
        points[second], points[(second + 1) % count],
      )) {
        return { valid: false, count, message: 'Edges cross each other. Drag a corner to untangle the shape.' };
      }
    }
  }
  return { valid: true, count, message: '' };
}

export function ringToGeoJson(points) {
  const ring = points.map((point) => [Number(point.lng.toFixed(7)), Number(point.lat.toFixed(7))]);
  return { type: 'Polygon', coordinates: [[...ring, ring[0]]] };
}

export function geoJsonToRing(geometry) {
  const ring = geometry?.type === 'Polygon' ? geometry.coordinates?.[0] || [] : [];
  const open = ring.length > 1
    && ring[0][0] === ring[ring.length - 1][0]
    && ring[0][1] === ring[ring.length - 1][1]
    ? ring.slice(0, -1)
    : ring;
  return open.map(([lng, lat]) => ({ lat, lng }));
}

function coordinateBounds(coordinates, bounds = { west: Infinity, south: Infinity, east: -Infinity, north: -Infinity }) {
  if (!Array.isArray(coordinates)) return bounds;
  if (typeof coordinates[0] === 'number') {
    bounds.west = Math.min(bounds.west, coordinates[0]);
    bounds.east = Math.max(bounds.east, coordinates[0]);
    bounds.south = Math.min(bounds.south, coordinates[1]);
    bounds.north = Math.max(bounds.north, coordinates[1]);
    return bounds;
  }
  coordinates.forEach((child) => coordinateBounds(child, bounds));
  return bounds;
}

// A saved area belongs to the open region when its center falls inside the
// region's bounding box; areas are operator-owned, not tied to one market row.
export function zonesInRegion(zones, regionGeometry) {
  const bounds = coordinateBounds(regionGeometry?.coordinates);
  if (!Number.isFinite(bounds.west)) return [];
  return (zones || []).filter((zone) => {
    const latitude = Number(zone.centroid_latitude);
    const longitude = Number(zone.centroid_longitude);
    return latitude >= bounds.south && latitude <= bounds.north
      && longitude >= bounds.west && longitude <= bounds.east;
  });
}
